import {
  glMatrix,
  type ReadonlyMat2d,
  type ReadonlyVec2,
  type ReadonlyVec4,
  vec2,
  vec4,
} from 'gl-matrix';

export type Axis = 'x' | 'y';

const X_POS_IDX = 0;
const Y_POS_IDX = 1;
const X_OFFSET_IDX = 2;
const Y_OFFSET_IDX = 3;

const vecFlyweight1 = vec4.create();
const vecFlyweight2 = vec4.create();

/**
 * Represents a 2D point in a graphic.
 *
 * This point supports non-uniform scaling; each component of the point has a
 * - position representing a location that scales with the size of the graphic
 * - offset representing a fixed distance from that position that does not scale
 *
 * e.g. If a point has x position 0.5 and x offset 15, it will always appear 15
 * units left of the center of the shape horizontally.
 */
export class ReadonlyGraphicPoint {
  protected constructor(protected readonly vector: ReadonlyVec4) {}

  /**
   * Calculates the actual position of a point given the size of the graphic,
   * with offsets scaled down to match a proportion of the graphic size.
   *
   * e.g. If a point has x position 0.5 and x offset 15, and the width is 100,
   * the output x will be 0.65.
   *
   * @param out A vec2 to store the result in
   * @param size The size of the graphic
   *
   * @returns The position in normalised scale
   */
  resolveToNormalisedScale(out: vec2, size: ReadonlyVec2): vec2 {
    return vec2.set(
      out,
      // At a size of 0, disable offsets to prevent odd behaviour
      size[0] ? this.xPos() + this.xOffset() / size[0] : this.xPos(),
      size[1] ? this.yPos() + this.yOffset() / size[1] : this.yPos(),
    );
  }

  resolveToNormalisedScaleWithTransform(
    out: vec2,
    size: ReadonlyVec2,
    transform: ReadonlyMat2d,
  ): vec2 {
    const normalisedPoint = this.resolveToNormalisedScale(out, size);
    return vec2.transformMat2d(out, normalisedPoint, transform);
  }

  /**
   * Calculates the actual position of a point given the size of the graphic,
   * with positions scaled up to match the graphic size.
   *
   * e.g. If a point has x position 0.5 and x offset 15, and the width is 100,
   * the output x will be 65.
   *
   * @param out A vec2 to store the result in
   * @param size The size of the graphic
   *
   * @returns The position in world scale
   */
  resolveToWorldScale(out: vec2, size: ReadonlyVec2): vec2 {
    return vec2.set(
      out,
      this.xPos() * size[0] + this.xOffset(),
      this.yPos() * size[1] + this.yOffset(),
    );
  }

  resolveToWorldScaleWithTransform(
    out: vec2,
    size: ReadonlyVec2,
    transform: ReadonlyMat2d,
  ): vec2 {
    const worldPoint = this.resolveToWorldScale(out, size);
    return vec2.transformMat2d(out, worldPoint, transform);
  }

  /**
   * Calculates the actual position of a point assuming a size of [1, 1], by
   * simply adding the positions and offsets together. This is usually only
   * valid during graphic building, when positions aren't yet normalised.
   *
   * e.g. If a point has x position 50 and x offset 15, the output x will be 65.
   *
   * @param out A vec2 to store the result in
   * @returns The position in world scale
   */
  resolveAtUnitSize(out: vec2): vec2 {
    return vec2.set(
      out,
      this.resolveAtUnitSizeForAxis('x'),
      this.resolveAtUnitSizeForAxis('y'),
    );
  }

  /**
   * Calculates the actual position of a single axis of a point assuming a size
   * of [1, 1], by simply adding the positions and offsets together. This is
   * the single-axis version of {@link resolveAtUnitSize}.
   *
   * @param axis The axis to resolve
   * @returns The position in world scale on the specified axis
   */
  resolveAtUnitSizeForAxis(axis: Axis): number {
    return axis === 'x' ? this.xPos() + this.xOffset() : this.yPos() + this.yOffset();
  }

  /**
   * Get the position (which scales with graphic size) of a point
   *
   * @param out A vec2 to store the result in
   * @returns The position
   */
  pos(out: vec2): vec2 {
    return vec2.set(out, this.xPos(), this.yPos());
  }

  /**
   * Get the offset (which does not scale with graphic size) of a point
   *
   * @param out A vec2 to store the result in
   * @returns The offset
   */
  offset(out: vec2): vec2 {
    return vec2.set(out, this.xOffset(), this.yOffset());
  }

  xPos(): number {
    return this.vector[X_POS_IDX];
  }

  yPos(): number {
    return this.vector[Y_POS_IDX];
  }

  xOffset(): number {
    return this.vector[X_OFFSET_IDX];
  }

  yOffset(): number {
    return this.vector[Y_OFFSET_IDX];
  }

  /**
   * Returns whether two points are equal in all components
   *
   * @param a The first point
   * @param b The second point
   * @returns True iff all components are equal
   */
  static equals(a: ReadonlyGraphicPoint, b: ReadonlyGraphicPoint): boolean {
    return vec4.equals(a.vector, b.vector);
  }

  /**
   * Returns whether two points form a vertical line, i.e. their x-components
   * are equal
   *
   * @param a The first point
   * @param b The second point
   * @returns True iff their x-components are equal
   */
  static isVertical(a: ReadonlyGraphicPoint, b: ReadonlyGraphicPoint): boolean {
    return (
      glMatrix.equals(a.xPos(), b.xPos()) && glMatrix.equals(a.xOffset(), b.xOffset())
    );
  }

  /**
   * Returns whether two points form a horizontal line, i.e. their y-components
   * are equal
   *
   * @param a The first point
   * @param b The second point
   * @returns True iff their y-components are equal
   */
  static isHorizontal(a: ReadonlyGraphicPoint, b: ReadonlyGraphicPoint): boolean {
    return (
      glMatrix.equals(a.yPos(), b.yPos()) && glMatrix.equals(a.yOffset(), b.yOffset())
    );
  }

  /**
   * See {@link GraphicPoint.copy} for public-facing function
   */
  protected static copyInternal(out: vec4, p: ReadonlyGraphicPoint): void {
    vec4.copy(out, p.vector);
  }

  /**
   * See {@link GraphicPoint.add} for public-facing function
   */
  protected static addInternal(
    out: vec4,
    a: ReadonlyGraphicPoint,
    b: ReadonlyGraphicPoint,
  ): void {
    vec4.add(out, a.vector, b.vector);
  }

  /**
   * See {@link GraphicPoint.subtract} for public-facing function
   */
  protected static subtractInternal(
    out: vec4,
    a: ReadonlyGraphicPoint,
    b: ReadonlyGraphicPoint,
  ): void {
    vec4.subtract(out, a.vector, b.vector);
  }

  /**
   * See {@link GraphicPoint.dividePosition} for public-facing function
   */
  protected static dividePositionInternal(
    out: vec4,
    p: ReadonlyGraphicPoint,
    divisor: ReadonlyVec2,
  ): void {
    out[X_POS_IDX] = p.xPos() / divisor[0];
    out[Y_POS_IDX] = p.yPos() / divisor[1];
    out[X_OFFSET_IDX] = p.xOffset();
    out[Y_OFFSET_IDX] = p.yOffset();
  }

  /**
   * See {@link GraphicPoint.interpolate} for public-facing function
   */
  protected static interpolateInternal(
    out: vec4,
    a: ReadonlyGraphicPoint,
    b: ReadonlyGraphicPoint,
    t: number,
  ): void {
    const point = vec4.scale(out, a.vector, 1 - t);
    vec4.scaleAndAdd(point, point, b.vector, t);
  }

  /**
   * See {@link GraphicPoint.interpolateByAxis} for public-facing function
   */
  protected static interpolateByAxisInternal(
    out: vec4,
    a: ReadonlyGraphicPoint,
    b: ReadonlyGraphicPoint,
    t: ReadonlyVec2,
  ): void {
    const t1 = vec4.set(vecFlyweight1, 1 - t[0], 1 - t[1], 1 - t[0], 1 - t[1]);
    vec4.multiply(t1, a.vector, t1);

    const t2 = vec4.set(vecFlyweight2, t[0], t[1], t[0], t[1]);
    vec4.multiply(t2, b.vector, t2);

    vec4.add(out, t1, t2);
  }

  /**
   * See {@link GraphicPoint.inverse} for public-facing function
   *
   * To explain the derivations of how each component is modified:
   *
   * Positions - When a point has no offsets, i.e. it is scaled proportionally
   * to the size of the shape, then its inverse is the same as the inverse for
   * a regular vector, so the x and y pos are set to the multiplicative inverse
   * of the original point.
   *
   * Offsets - We have the constraint that
   * `inverse(p).resolveToWorldScale(p.resolveToWorldScale(s)) = s`:
   * ```
   * inverse(p).resolveToWorldScale(p.resolveToWorldScale(s)) = s
   * -> inverse(p).resolveToWorldScale(pos_p * s + offset_p) = s
   * -> pos_inverse_p * (pos_p * s + offset_p) + offset_inverse_p = s
   * -> pos_inverse_p * pos_p * s + pos_inverse_p * offset_p + offset_inverse_p = s
   * ```
   * but we already know that `pos_inverse_p = 1 / pos_p`, so
   * ```
   * (1 / pos_p) * pos_p * s + (1 / pos_p) * offset_p + offset_inverse_p = s
   * -> s + offset_p / pos_p + offset_inverse_p = s
   * -> offset_p / pos_p + offset_inverse_p = 0
   * -> offset_inverse_p = -offset_p / pos_p
   * ```
   */
  protected static inverseInternal(out: vec4, p: ReadonlyGraphicPoint): void {
    const [xPos, yPos, xOffset, yOffset] = p.vector;
    vec4.set(out, 1 / xPos, 1 / yPos, -xOffset / xPos, -yOffset / yPos);
  }
}

export class GraphicPoint extends ReadonlyGraphicPoint {
  static readonly ZERO: ReadonlyGraphicPoint = GraphicPoint.fromPositionsOnly(
    GraphicPoint.create(),
    [0, 0],
  );

  static readonly UNIT: ReadonlyGraphicPoint = GraphicPoint.fromPositionsOnly(
    GraphicPoint.create(),
    [1, 1],
  );

  static readonly HALF_UNIT: ReadonlyGraphicPoint = GraphicPoint.fromPositionsOnly(
    GraphicPoint.create(),
    [0.5, 0.5],
  );

  private constructor(protected readonly vector: vec4) {
    super(vector);
  }

  /**
   * Create a new GraphicPoint with all positions and offsets set to 0.
   *
   * @returns The new point
   */
  static create(): GraphicPoint {
    return new GraphicPoint(vec4.create());
  }

  /**
   * Populate a GraphicPoint with the provided positions and offsets.
   *
   * @param out The point to populate
   * @param pos The positions
   * @param offset The offsets
   * @returns The populated point
   */
  static fromPositionsAndOffsets(
    out: GraphicPoint,
    pos: ReadonlyVec2,
    offset: ReadonlyVec2,
  ): GraphicPoint {
    vec4.set(out.vector, pos[0], pos[1], offset[0], offset[1]);
    return out;
  }

  /**
   * Populate a GraphicPoint with the provided positions, setting all offsets to zero.
   *
   * @param out The point to populate
   * @param pos The positions
   * @returns The populated point
   */
  static fromPositionsOnly(out: GraphicPoint, pos: ReadonlyVec2): GraphicPoint {
    vec4.set(out.vector, pos[0], pos[1], 0, 0);
    return out;
  }

  /**
   * Populate a GraphicPoint with the provided positions, setting all offsets to zero.
   *
   * @param out The point to populate
   * @param offset The offsets
   * @returns The populated point
   */
  static fromOffsetsOnly(out: GraphicPoint, offset: ReadonlyVec2): GraphicPoint {
    vec4.set(out.vector, 0, 0, offset[0], offset[1]);
    return out;
  }

  /**
   * Populate a GraphicPoint from raw values.
   *
   * @param out The point to populate
   * @param xPos The position in the X-axis
   * @param yPos The position in the Y-axis
   * @param xOffset The offset in the X-axis
   * @param yOffset The offset in the Y-axis
   * @returns The populated point
   */
  static set(
    out: GraphicPoint,
    xPos: number,
    yPos: number,
    xOffset: number,
    yOffset: number,
  ): GraphicPoint {
    vec4.set(out.vector, xPos, yPos, xOffset, yOffset);
    return out;
  }

  /**
   * Populate a GraphicPoint's position and offset for a single axis from raw
   * values.
   *
   * @param out The point to modify
   * @param axis The axis to modify
   * @param pos The position in the specified axis
   * @param offset The offset in the specified axis
   * @returns The modified point
   */
  static setForAxis(
    out: GraphicPoint,
    axis: Axis,
    pos: number,
    offset: number,
  ): GraphicPoint {
    if (axis === 'x') {
      out.vector[X_POS_IDX] = pos;
      out.vector[X_OFFSET_IDX] = offset;
    } else {
      out.vector[Y_POS_IDX] = pos;
      out.vector[Y_OFFSET_IDX] = offset;
    }
    return out;
  }

  /**
   * Set all components of a GraphicPoint to zero.
   *
   * @param out The point to set components of
   * @returns The point
   */
  static zero(out: GraphicPoint): GraphicPoint {
    vec4.zero(out.vector);
    return out;
  }

  /**
   * Populate a GraphicPoint from the contents of another point.
   *
   * @param out The point to populate
   * @param p The point to copy
   * @returns The populated point
   */
  static copy(out: GraphicPoint, p: ReadonlyGraphicPoint): GraphicPoint {
    ReadonlyGraphicPoint.copyInternal(out.vector, p);
    return out;
  }

  /**
   * Populate a GraphicPoint from the contents of other points, taking only the
   * components of a single axis. Each axis can be sourced from a different
   * point, or not provided to preserve the current values.
   *
   * @param out The point to store the result in
   * @param sources The points to take components for each axis from
   * @returns The point the result was stored in
   */
  static copyPointsByAxis(
    out: GraphicPoint,
    sources: {
      x?: ReadonlyGraphicPoint;
      y?: ReadonlyGraphicPoint;
    },
  ): GraphicPoint {
    if (sources.x) {
      GraphicPoint.setForAxis(out, 'x', sources.x.xPos(), sources.x.xOffset());
    }

    if (sources.y) {
      GraphicPoint.setForAxis(out, 'y', sources.y.yPos(), sources.y.yOffset());
    }

    return out;
  }

  /**
   * Adds two points together component-wise, storing the result in a provided
   * point.
   *
   * In-place modification is possible by passing a point to both the `out` and
   * `a` or `b` parameters.
   *
   * @param out The point to store the result in
   * @param a The first point to add
   * @param b The second point to add
   * @returns The point the result was stored in
   */
  static add(
    out: GraphicPoint,
    a: ReadonlyGraphicPoint,
    b: ReadonlyGraphicPoint,
  ): GraphicPoint {
    ReadonlyGraphicPoint.addInternal(out.vector, a, b);
    return out;
  }

  /**
   * Subtracts the second point from the first component-wise, storing the
   * result in a provided point.
   *
   * In-place modification is possible by passing a point to both the `out` and
   * `a` or `b` parameters.
   *
   * @param out The point to store the result in
   * @param a The point to subtract from
   * @param b The point to subtract by
   * @returns The point the result was stored in
   */
  static subtract(
    out: GraphicPoint,
    a: ReadonlyGraphicPoint,
    b: ReadonlyGraphicPoint,
  ): GraphicPoint {
    ReadonlyGraphicPoint.subtractInternal(out.vector, a, b);
    return out;
  }

  /**
   * Divides a point's positions by a 2D vector component-wise, storing the
   * result in a provided point.
   *
   * In-place modification is possible by passing a point to both the `out` and
   * `p` parameters.
   *
   * @param out The point to store the result in
   * @param p The point to divide positions of
   * @param divisor The vector to divide by
   * @returns The point the result was stored in
   */
  static dividePosition(
    out: GraphicPoint,
    p: ReadonlyGraphicPoint,
    divisor: ReadonlyVec2,
  ): GraphicPoint {
    ReadonlyGraphicPoint.dividePositionInternal(out.vector, p, divisor);
    return out;
  }

  /**
   * Interpolates between two points component-wise, storing the result in a
   * provided point.
   *
   * In-place modification is possible by passing a point to both the `out` and
   * `a` or `b` parameters.
   *
   * @param out The point to store the result in
   * @param a The first point
   * @param b The second point
   * @param t The proportion from `a` to `b` that the result should be. Values
   * from 0 to 1 will fall on the interval between `a` and `b`.
   *
   * @returns The point the result was stored in
   */
  static interpolate(
    out: GraphicPoint,
    a: ReadonlyGraphicPoint,
    b: ReadonlyGraphicPoint,
    t: number,
  ): GraphicPoint {
    ReadonlyGraphicPoint.interpolateInternal(out.vector, a, b, t);
    return out;
  }

  /**
   * Interpolates between two points with each axis interpolating by a separate
   * value, storing the result in a provided point.
   *
   * In-place modification is possible by passing a point to both the `out` and
   * `a` or `b` parameters.
   *
   * @param out The point to store the result in
   * @param a The first point
   * @param b The second point
   * @param t The 2D proportion from `a` to `b` that the result should be. Values
   * from 0 to 1 will fall on the interval between `a` and `b`. The first value
   * controls the X-axis, the second controls the Y-axis.
   *
   * @returns The point the result was stored in
   */
  static interpolateByAxis(
    out: GraphicPoint,
    a: ReadonlyGraphicPoint,
    b: ReadonlyGraphicPoint,
    t: ReadonlyVec2,
  ): GraphicPoint {
    ReadonlyGraphicPoint.interpolateByAxisInternal(out.vector, a, b, t);
    return out;
  }

  /**
   * Calculates the inverse of a GraphicPoint. If a GraphicPoint is considered
   * as a function that maps a vec2 (the shape size) to another vec2 (a position),
   * the inverse is also a GraphicPoint that maps one vec2 to another. This
   * allows us to use GraphicPoints to represent distances that scale
   * non-proportionally on shape size, e.g. the size of a text area.
   *
   * Notably:
   * - `inverse(inverse(p)) = p`
   * - `inverse(p).resolveToWorldScale(p.resolveToWorldScale(s)) = s`
   *
   * @param out The point to store the result in
   * @param p The point to take the inverse of
   * @returns The point the result was stored in
   */
  static inverse(out: GraphicPoint, p: ReadonlyGraphicPoint): GraphicPoint {
    ReadonlyGraphicPoint.inverseInternal(out.vector, p);
    return out;
  }
}
